import { useEffect, useState } from 'react';
import {
  type Entity,
  RELATION_PROVIDES_API,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import type { AlphaEntity } from '@backstage/catalog-model/alpha';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  allTargetsOf,
  consumersOfProvidedApis,
  RELATED_FIELDS,
  relationsOf,
  targetsOf,
  unresolvedOf,
  type ComponentRelations,
} from './relations';

/** A processing error on the entity or on one of the locations that bring it. */
export type ProcessingError = {
  /** The entity it was reported on — the component itself, or an ancestor. */
  on: string;
  message: string;
};

/** An API the component provides, as the facts bar names it. */
export type ProvidedApi = {
  ref: string;
  name: string;
  title?: string;
  type?: string;
  /** The definition's own version (`info.version`), where one is found. */
  version?: string;
  /** The definition as the catalog holds it, once asked for. */
  definition?: string;
};

export type ComponentCatalog = {
  loading: boolean;
  error?: Error;
  relations?: ComponentRelations;
  providedApis: ProvidedApi[];
  /** References this entity names that the catalog does not hold. */
  unresolved: string[];
  processingErrors: ProcessingError[];
  /**
   * Whether the provided APIs' definitions have been asked for, and whether
   * that failed. They are asked for once, for the facts bar's versions, and
   * the API tab reads the same answer rather than asking again.
   */
  definitions: 'loading' | 'loaded' | 'failed';
};

// The status item type the catalog writes processing errors under.
const PROCESSING_ERROR_TYPE = 'backstage.io/catalog-processing';

/**
 * The version an API definition states for itself. `info.version` in OpenAPI
 * and AsyncAPI, read without parsing the whole document — in YAML it is the
 * first `version:` key in the file, in JSON the first `"version"`, and both
 * formats put `info` ahead of the paths by convention. No match, no version:
 * the facts bar then names the API without one rather than guessing.
 */
export function definitionVersion(definition: unknown): string | undefined {
  if (typeof definition !== 'string') return undefined;
  const yaml = /^\s+version:\s*['"]?([^'"\s#]+)/m.exec(definition);
  const json = /"version"\s*:\s*"([^"]+)"/.exec(definition);
  return (yaml ?? json)?.[1];
}

const EMPTY = {
  unresolved: [],
  processingErrors: [],
  providedApis: [],
  definitions: 'loading' as const,
};

/**
 * Everything the component page reads from the catalog beyond the entity
 * itself: who it depends on and who depends on it, what it names that does not
 * exist, and what went wrong reading it (specs/component-page.md).
 *
 * The related entities are asked for in two rounds. The first returns every
 * entity the component names — which is also what the banner checks, the same
 * way the catalog's own relation warning does. The second asks who calls the
 * APIs it provides, which only the API entities know.
 */
export function useComponentCatalog(entity: Entity): ComponentCatalog {
  const catalogApi = useApi(catalogApiRef);
  const ref = stringifyEntityRef(entity);
  const [state, setState] = useState<ComponentCatalog>({
    loading: true,
    ...EMPTY,
  });

  useEffect(() => {
    let active = true;
    setState({ loading: true, ...EMPTY });
    (async () => {
      try {
        const found = new Map<string, Entity | undefined>();
        const ask = async (refs: string[]) => {
          const fresh = refs.filter(r => !found.has(r));
          if (!fresh.length) return;
          const { items } = await catalogApi.getEntitiesByRefs({
            entityRefs: fresh,
            fields: RELATED_FIELDS,
          });
          fresh.forEach((r, i) => found.set(r, items[i] ?? undefined));
        };

        const named = allTargetsOf(entity);
        await ask(named);
        await ask(consumersOfProvidedApis(entity, found));

        // The relations stand as soon as the catalog has named them. The
        // versions of the APIs it provides and its processing errors follow,
        // asked for at once and each on its own: a slow or failing one holds
        // back nothing else, and costs only what it would have added.
        const apiRefs = targetsOf(entity, RELATION_PROVIDES_API);
        const providedApis: ProvidedApi[] = apiRefs.flatMap(apiRef => {
          const api = found.get(apiRef);
          if (!api) return [];
          const spec = (api.spec ?? {}) as Record<string, unknown>;
          return [
            {
              ref: apiRef,
              name: api.metadata.name,
              title: api.metadata.title,
              type: typeof spec.type === 'string' ? spec.type : undefined,
            },
          ];
        });
        if (!active) return;
        setState({
          loading: false,
          relations: relationsOf(entity, found),
          providedApis,
          unresolved: unresolvedOf(named, found),
          processingErrors: [],
          definitions: 'loading',
        });

        // Each lookup lands the moment it answers: a slow ancestry must not
        // hold back the API tab, nor a slow definition the defects banner.
        const versions = (
          apiRefs.length
            ? catalogApi.getEntitiesByRefs({
                entityRefs: apiRefs,
                fields: ['spec.definition'],
              })
            : Promise.resolve({ items: [] as (Entity | undefined)[] })
        ).then(
          ({ items }) => {
            if (!active) return;
            const versioned = providedApis.map(api => {
              const i = apiRefs.indexOf(api.ref);
              const definition = (
                items[i]?.spec as Record<string, unknown> | undefined
              )?.definition;
              return {
                ...api,
                version: definitionVersion(definition),
                definition:
                  typeof definition === 'string' ? definition : undefined,
              };
            });
            setState(current => ({
              ...current,
              providedApis: versioned,
              definitions: 'loaded',
            }));
          },
          () => {
            if (active) {
              setState(current => ({ ...current, definitions: 'failed' }));
            }
          },
        );
        const ancestry = catalogApi.getEntityAncestors({ entityRef: ref }).then(
          ancestors => {
            if (!active) return;
            const processingErrors = ancestors.items.flatMap(item =>
              ((item.entity as AlphaEntity).status?.items ?? [])
                .filter(
                  status =>
                    status.level === 'error' &&
                    status.type === PROCESSING_ERROR_TYPE &&
                    status.error,
                )
                .map(status => ({
                  on: stringifyEntityRef(item.entity),
                  message: status.error?.message ?? status.message,
                })),
            );
            setState(current => ({ ...current, processingErrors }));
          },
          // Without an answer the banner shows the defects it did learn of.
          () => undefined,
        );
        await Promise.all([versions, ancestry]);
      } catch (err) {
        if (active) {
          setState({ loading: false, error: err as Error, ...EMPTY });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [entity, ref, catalogApi]);

  return state;
}
